import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  PieChart,
  Target,
  Calendar, ImageIcon,
  TrendingUp,
  Activity,
  Database,
  Eye, Filter
} from "lucide-react";
import { message, Spin, Select } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Cell, Area,
  AreaChart,
  Legend,
  Pie
} from "recharts";
import { useLocation } from "react-router-dom";
import axios from "../../utils/axios";

const { Option } = Select;

const Analytics = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [analyticsData, setAnalyticsData] = useState({
    projectStats: [],
    classDistribution: [],
    timelineData: [],
    totalStats: {
      totalProjects: 0,
      totalImages: 0,
      totalAnnotations: 0,
      averageAnnotationsPerImage: 0
    }
  });
  const user = JSON.parse(localStorage.getItem("user"));
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") ||
    "";
  const organizationId = localStorage.getItem("organizationId");

  // Color palette for charts
  const COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/fetch-annotation-image", {
        username: user.username,
        projectname: projectname,
        organizationId
      });

      if (response.status === 200) {
        const allAnnotations = response.data.annotationImage || [];
        
        // Format projects data
        const formattedProjects = allAnnotations.map(item => ({
          projectName: item.projectname,
          annotationGroup: item.annotationGroup,
          images: item.images.map(img => img.imageUrl),
          annotations: item.images.map(img => ({
            imageUrl: img.imageUrl,
            annotations: img.annotations || []
          })),
          createdOn: item.createdOn
        }));

        setProjects(formattedProjects);
        processAnalyticsData(formattedProjects);
      }
    } catch (error) {
      message.error("Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (projectsData) => {
    // Project statistics
    const projectStats = projectsData.map(project => {
      const totalAnnotations = project.annotations.reduce(
        (total, img) => total + (img.annotations ? img.annotations.length : 0), 0
      );
      const annotatedImages = project.annotations.filter(
        img => img.annotations && img.annotations.length > 0
      ).length;
      
      return {
        name: project.projectName.length > 15 
          ? project.projectName.substring(0, 15) + '...' 
          : project.projectName,
        fullName: project.projectName,
        totalImages: project.images.length,
        annotatedImages,
        totalAnnotations,
        averagePerImage: project.images.length > 0 
          ? (totalAnnotations / project.images.length).toFixed(1) 
          : 0,
        group: project.annotationGroup,
        createdOn: project.createdOn
      };
    });

    // Class distribution
    const classMap = {};
    projectsData.forEach(project => {
      project.annotations.forEach(img => {
        if (img.annotations) {
          img.annotations.forEach(annotation => {
            const className = annotation.class || 'Unlabeled';
            classMap[className] = (classMap[className] || 0) + 1;
          });
        }
      });
    });

    const classDistribution = Object.entries(classMap)
      .map(([className, count]) => ({
        name: className,
        value: count,
        percentage: ((count / Object.values(classMap).reduce((a, b) => a + b, 0)) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);

    // Timeline data (projects created over time)
    const timelineMap = {};
    projectsData.forEach(project => {
      if (project.createdOn) {
        const date = new Date(project.createdOn).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        });
        timelineMap[date] = (timelineMap[date] || 0) + 1;
      }
    });

    const timelineData = Object.entries(timelineMap)
      .map(([date, count]) => ({ date, projects: count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Total statistics
    const totalImages = projectsData.reduce((total, project) => total + project.images.length, 0);
    const totalAnnotations = projectsData.reduce((total, project) => 
      total + project.annotations.reduce((sum, img) => 
        sum + (img.annotations ? img.annotations.length : 0), 0
      ), 0
    );

    setAnalyticsData({
      projectStats,
      classDistribution,
      timelineData,
      totalStats: {
        totalProjects: projectsData.length,
        totalImages,
        totalAnnotations,
        averageAnnotationsPerImage: totalImages > 0 ? (totalAnnotations / totalImages).toFixed(1) : 0
      }
    });
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  // Filter projects based on selected criteria
  useEffect(() => {
    let filtered = [...projects];

    // Filter by annotation group
    if (selectedGroup !== 'all') {
      filtered = filtered.filter(project => project.annotationGroup === selectedGroup);
    }

    // Filter by time range
    if (selectedTimeRange !== 'all') {
      const now = new Date();
      const timeRanges = {
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
        '90d': 90 * 24 * 60 * 60 * 1000
      };
      
      if (timeRanges[selectedTimeRange]) {
        const cutoffDate = new Date(now.getTime() - timeRanges[selectedTimeRange]);
        filtered = filtered.filter(project => 
          project.createdOn && new Date(project.createdOn) >= cutoffDate
        );
      }
    }
    processAnalyticsData(filtered);
  }, [selectedTimeRange, selectedGroup, projects]);

  const getUniqueGroups = () => {
    const groups = [...new Set(projects.map(p => p.annotationGroup))];
    return groups.filter(Boolean);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-700">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-600 text-sm font-medium mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mb-1">{value}</p>
          <p className="text-slate-500 text-xs">{subtitle}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center text-emerald-600 text-sm">
          <TrendingUp className="w-4 h-4 mr-1" />
          <span>{trend}</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              {/* <button
                onClick={() => navigate(-1)}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-0.5 transition-transform" />
                <span className="font-medium">Back</span>
              </button> */}
              {/* <div className="h-6 w-px bg-slate-300"></div> */}
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                  Annotation Analytics
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Insights into your annotation projects</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <Select
                  value={selectedGroup}
                  onChange={setSelectedGroup}
                  placeholder="Filter by group"
                  className="min-w-32"
                  size="middle"
                >
                  <Option value="all">All Groups</Option>
                  {getUniqueGroups().map(group => (
                    <Option key={group} value={group}>{group}</Option>
                  ))}
                </Select>
              </div>
              
              <Select
                value={selectedTimeRange}
                onChange={setSelectedTimeRange}
                placeholder="Time range"
                className="min-w-32"
                size="middle"
              >
                <Option value="all">All Time</Option>
                <Option value="7d">Last 7 days</Option>
                <Option value="30d">Last 30 days</Option>
                <Option value="90d">Last 90 days</Option>
              </Select>

            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Projects"
            value={analyticsData.totalStats.totalProjects}
            subtitle="Annotation projects created"
            icon={Database}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            trend="+12% this month"
          />
          <StatCard
            title="Total Images"
            value={analyticsData.totalStats.totalImages}
            subtitle="Images uploaded for annotation"
            icon={ImageIcon}
            color="bg-gradient-to-br from-emerald-500 to-emerald-600"
            trend="+8% this week"
          />
          <StatCard
            title="Total Annotations"
            value={analyticsData.totalStats.totalAnnotations}
            subtitle="Bounding boxes created"
            icon={Target}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            trend="+25% this month"
          />
          <StatCard
            title="Avg. Annotations/Image"
            value={analyticsData.totalStats.averageAnnotationsPerImage}
            subtitle="Average annotations per image"
            icon={Activity}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
            trend="↑ 0.3 from last month"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Project Annotations Chart */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Annotations per Project
              </h2>
              <div className="text-sm text-slate-500">
                {analyticsData.projectStats.length} projects
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.projectStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="totalAnnotations" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Class Distribution Chart */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-emerald-600" />
                Class Distribution
              </h2>
              <div className="text-sm text-slate-500">
                {analyticsData.classDistribution.length} classes
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  dataKey="value"
                  data={analyticsData.classDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {analyticsData.classDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`${value} (${analyticsData.classDistribution.find(item => item.name === name)?.percentage}%)`, 'Annotations']}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span className="text-sm text-slate-700">{value}</span>}
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Project Timeline and Details */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Timeline Chart */}
          <div className="xl:col-span-2 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                Project Creation Timeline
              </h2>
              <div className="text-sm text-slate-500">
                Monthly breakdown
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={analyticsData.timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="projects" 
                  stroke="#8b5cf6" 
                  fill="#8b5cf6" 
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Top Projects */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                Top Projects
              </h2>
              <button 
                onClick={() => navigate("/sidebar/ViewAnnotation")}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                <Eye className="w-4 h-4 mr-1" />
                View All
              </button>
            </div>
            
            <div className="space-y-4 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
              {analyticsData.projectStats
                .toSorted((a, b) => b.totalAnnotations - a.totalAnnotations)
                .slice(0, 5)
                .map((project, index) => (
                  <div key={project.fullName} className="flex items-center justify-between p-4 rounded-xl border bg-gradient-to-br from-slate-50 to-white border-slate-200/60 hover:border-slate-300/60 transition-all duration-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-slate-700" title={project.fullName}>
                          {project.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {project.group}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-emerald-600">
                        {project.totalAnnotations}
                      </div>
                      <div className="text-xs text-slate-500">
                        annotations
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Detailed Class Breakdown */}
        {analyticsData.classDistribution.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center">
                <Target className="w-5 h-5 mr-2 text-orange-600" />
                Detailed Class Breakdown
              </h2>
              <div className="text-sm text-slate-500">
                All annotation classes
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {analyticsData.classDistribution.map((classItem, index) => (
                <div key={classItem.name} className="p-4 rounded-xl border bg-gradient-to-br from-white to-slate-50 border-slate-200/60 hover:border-slate-300/60 transition-all duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm text-slate-700 truncate" title={classItem.name}>
                      {classItem.name}
                    </div>
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                  </div>
                  <div className="text-2xl font-bold text-slate-800 mb-1">
                    {classItem.value}
                  </div>
                  <div className="text-xs text-slate-500">
                    {classItem.percentage}% of total
                  </div>
                  <div className="mt-2 w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${classItem.percentage}%`, 
                        backgroundColor: COLORS[index % COLORS.length] 
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;